// Per-browser preferences: language, theme, last chosen center (SPEC §7.2, §9.2, §10.1).
// Storage may be unavailable (private windows); every access falls back to defaults.

export type Language = "en" | "uk";
export type ThemeChoice = "light" | "dark" | "system";

const LANGUAGE_KEY: string = "webuniverse.language";
const THEME_KEY: string = "webuniverse.theme";
const CENTER_KEY: string = "webuniverse.center";

let currentLanguage: Language = readLanguage();
let currentTheme: ThemeChoice = readTheme();

export function getLanguage(): Language {
  return currentLanguage;
}

export function setLanguage(language: Language): void {
  currentLanguage = language;
  writeStored(LANGUAGE_KEY, language);
  if (typeof document !== "undefined") {
    document.documentElement.lang = language;
  }
}

export function getTheme(): ThemeChoice {
  return currentTheme;
}

/** Stores the theme and applies it: "system" removes the attribute so CSS follows the OS. */
export function setTheme(theme: ThemeChoice): void {
  currentTheme = theme;
  writeStored(THEME_KEY, theme);
  applyTheme();
}

export function applyTheme(): void {
  if (currentTheme === "system") {
    document.documentElement.removeAttribute("data-theme");
    return;
  }

  document.documentElement.setAttribute("data-theme", currentTheme);
}

export function getLastCenterId(): string | null {
  return readStored(CENTER_KEY);
}

export function setLastCenterId(centerId: string): void {
  writeStored(CENTER_KEY, centerId);
}

function readLanguage(): Language {
  const stored: string | null = readStored(LANGUAGE_KEY);
  return stored === "uk" ? "uk" : "en";
}

function readTheme(): ThemeChoice {
  const stored: string | null = readStored(THEME_KEY);
  if (stored === "light" || stored === "dark") {
    return stored;
  }

  return "system";
}

function readStored(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage blocked: the preference lasts for this page only.
  }
}
